<template>
  <layout-report-step
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :image="header.image"
    :debug="header.debug"
  >
    <v-card class="pt-4 pr-4 pl-4" flat max-width="800">
      <v-form v-model="valid">
        <div class="caption mb-4">{{ $t("report.currentLocation.locationTitle") }}</div>
        <v-radio-group v-model="isAtVehicleLocation" @change="getPositionIfTrue">
          <v-radio :label="$t('report.currentLocation.isAtLocation')" :value="true"></v-radio>
          <v-radio :label="$t('report.currentLocation.isNotAtLocation')" :value="false"></v-radio>
        </v-radio-group>

        <div v-if="isAtVehicleLocation !== undefined">
          <div class="caption" v-if="isLoadingGeo">
            <v-progress-circular :size="20" indeterminate :color="color"></v-progress-circular>
            <span>{{ $t("report.currentLocation.geoLoading") }}</span>
          </div>
          <div class="caption mb-6" v-else-if="address.geo.lat && address.geo.lng && isAtVehicleLocation">
            <v-icon color="success">mdi-map-marker</v-icon>
            <span
              >{{ $t("report.currentLocation.geoSuccess") }} ({{
                address.geo.lat.toLocaleString("de-DE", { minimumFractionDigits: 6, maximumFractionDigits: 6 })
              }},
              {{
                address.geo.lng.toLocaleString("de-DE", { minimumFractionDigits: 6, maximumFractionDigits: 6 })
              }})</span
            >
          </div>
          <div class="caption mb-6" v-else-if="isGeoPermissionDenied && isAtVehicleLocation">
            <v-icon color="warning">mdi-alert-circle</v-icon>
            <span>{{ $t("report.currentLocation.geoPermissionDenied") }}</span>
            <v-btn class="mt-2" elevation="0" color="primary" @click="retryGeolocation">
              {{ $t("report.currentLocation.retryGeolocation") }}
            </v-btn>
          </div>
          <div class="caption mb-4">{{ $t("report.currentLocation.addressTitle") }}</div>

          <v-text-field
            :rules="requiredRule"
            v-model="address.street"
            outlined
            :label="$t('report.currentLocation.street')"
          >
          </v-text-field>
          <v-text-field
            :rules="rules(RulesEnum.REQUIRED_RULE, RulesEnum.ZIP_GERMANY_RULE)"
            v-model="address.zip"
            outlined
            :label="$t('report.currentLocation.zip')"
          >
          </v-text-field>
          <v-text-field
            :rules="requiredRule"
            v-model="address.city"
            outlined
            :label="$t('report.currentLocation.city')"
          >
          </v-text-field>
        </div>
      </v-form>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import { requiredRule } from "@/lib/rules/requiredRule";
import { handleError } from "@/lib/utility/handleError";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import RulesMixin from "@/mixins/RulesMixin.vue";
import { AddressWithGeoBase } from "@/models/address.entity";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportModule } from "@/store/modules/report.store";
import { mixins } from "vue-class-component";
import { Component } from "vue-property-decorator";

@Component({
  components: {
    LayoutReportStep
  }
})
export default class ReportCurrentLocation extends mixins(ReportStepMixin, RulesMixin) implements IReportStep {
  header: IReportHeaderData = {
    title: "report.currentLocation.title",
    description: "report.currentLocation.description"
  };

  screen = ReportScreenEnum.currentLocation;

  isLoadingGeo = false;
  isGeoPermissionDenied = false;

  mounted() {
    // Initialize the location in the store if it doesn't exist
    if (!ReportModule.location) {
      ReportModule.updateLocation(new AddressWithGeoBase());
    }
  }

  get isAtVehicleLocation(): boolean | undefined {
    return ReportModule.isAtVehicleLocation;
  }

  set isAtVehicleLocation(isAtVehicleLocation: boolean | undefined) {
    ReportModule.setIsAtVehicleLocation(isAtVehicleLocation);
  }

  get requiredRule() {
    return [requiredRule()];
  }

  get address() {
    return ReportModule.location || new AddressWithGeoBase();
  }

  set address(address: AddressWithGeoBase) {
    ReportModule.updateLocation({ ...address }); // Create a copy to ensure reactivity
  }

  valid = false;
  timepickerMenu = false;

  async getPositionIfTrue() {
    if (this.isAtVehicleLocation) {
      await this.getLocation();
    } else {
      this.address.geo.lat = 0;
      this.address.geo.lng = 0;
    }
  }

  async getLocation() {
    this.isLoadingGeo = true;
    this.isGeoPermissionDenied = false;
    try {
      const geo: any = await new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
          reject(new Error("Geolocation is not available."));
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve(pos);
          },
          (err) => {
            // Check if error is due to permission denial (code 1)
            if (err.code === 1) {
              this.isGeoPermissionDenied = true;
            }
            reject(err);
          }
        );
      });
      this.address.geo.lat = geo.coords.latitude;
      this.address.geo.lng = geo.coords.longitude;
    } catch (error) {
      handleError(error);
    } finally {
      this.isLoadingGeo = false;
    }
  }

  retryGeolocation() {
    this.getLocation();
  }

  get isDone() {
    return this.valid && this.isAtVehicleLocation !== undefined && !this.isLoadingGeo;
  }

  get color() {
    return getDefaultPartnerColor();
  }
}
</script>

<style scoped>
.center {
  text-align: center;
}
</style>
